/**
 * useAssetCatalog — carga el catálogo de DigitalAssets + DigitalAssetItems
 * del API y construye un mapa tagCode → { assetName, itemName }.
 *
 * La carga es paralela: primero trae todos los assets, luego en paralelo
 * trae los items de cada asset. El resultado se cachea en memoria mientras
 * el componente esté montado.
 *
 * Reemplaza los mapas hardcodeados TAG_LABELS y PRODUCT_CONFIG.
 */
import { useState, useEffect } from 'react'
import { listDigitalAssets, listDigitalAssetItems } from '@/features/ksec/api'

export interface AssetCatalogEntry {
  /** public_id del DigitalAssetItem — usar en createPrivilege */
  itemPublicId: string
  tagCode: string
  itemName: string
  assetId: string
  assetName: string
}

export interface AssetCatalog {
  /** Map: tagCode → AssetCatalogEntry */
  byTag: Map<string, AssetCatalogEntry>
  /** Lista de assets únicos presentes en el catálogo */
  assets: Array<{ id: string; name: string }>
  loading: boolean
  error: string | null
}

const EMPTY: AssetCatalog = {
  byTag: new Map(),
  assets: [],
  loading: true,
  error: null,
}

export function useAssetCatalog(): AssetCatalog {
  const [catalog, setCatalog] = useState<AssetCatalog>(EMPTY)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        // 1. Traer todos los DigitalAssets
        const assetsRes = await listDigitalAssets(100)
        const assets = assetsRes.data ?? []

        // 2. Traer items de todos los assets en paralelo
        const itemResults = await Promise.allSettled(
          assets.map((a) =>
            listDigitalAssetItems(a.id).then((res) => ({
              asset: a,
              items: res.data ?? [],
            })),
          ),
        )

        if (cancelled) return

        // 3. Construir el mapa tagCode → entry
        const byTag = new Map<string, AssetCatalogEntry>()
        const assetList: Array<{ id: string; name: string }> = []

        for (const result of itemResults) {
          if (result.status !== 'fulfilled') continue
          const { asset, items } = result.value
          assetList.push({ id: asset.id, name: asset.name })

          for (const item of items) {
            if (!item.tagCode) continue
            byTag.set(item.tagCode, {
              itemPublicId: item.id,
              tagCode: item.tagCode,
              itemName: item.name,
              assetId: asset.id,
              assetName: asset.name,
            })
          }
        }

        setCatalog({ byTag, assets: assetList, loading: false, error: null })
      } catch {
        if (!cancelled) {
          setCatalog((prev) => ({ ...prev, loading: false, error: 'No se pudo cargar el catálogo.' }))
        }
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  return catalog
}
